// ── guestUpgrade ─────────────────────────────────────────────────────────────
// A guest becomes a member IN PLACE: updateUser({ email }) on the anonymous user keeps
// the same user_id, so the library, marks and rooms are simply theirs. Lifted from
// Login.jsx's handleMagic (2026-10-14) so the in-app "Keep this list" sheet and the
// Login page share one path. Both halves are safe to call from a sheet: no navigation.
import { logEvent } from './analytics'

const PROD_URL = 'https://app.nextep.show'
const redirectTo = () => (typeof window !== 'undefined' && window.location.hostname === 'localhost') ? `${window.location.origin}/` : `${PROD_URL}/`

export const validEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(s || '').trim().toLowerCase())

// → { status: 'attached' | 'code_sent' | 'exists' | 'not_guest' | 'error', message? }
export async function sendUpgradeCode(supabase, emailRaw) {
  const addr = String(emailRaw || '').trim().toLowerCase()
  if (!validEmail(addr)) return { status: 'error', message: 'That doesn’t look like an email address.' }
  let cur = null
  try { cur = (await supabase.auth.getUser()).data?.user || null } catch {}
  if (!cur?.is_anonymous) return { status: 'not_guest' }
  const { data, error } = await supabase.auth.updateUser({ email: addr }, { emailRedirectTo: redirectTo() })
  if (error) {
    const m = error.message || ''
    if (/already registered|already exists|already in use|already been taken/i.test(m)) return { status: 'exists', message: 'That email already has a NextEp account. Sign in to it and your list comes along.' }
    if (/rate limit|60 seconds|too many|security purposes|temporarily/i.test(m)) return { status: 'error', message: 'Too many attempts just now. Give it a minute, then try again.' }
    return { status: 'error', message: 'Couldn’t send a code to that address. Check it and try again.' }
  }
  const uu = data?.user
  if (uu && !uu.is_anonymous && (uu.email || '').toLowerCase() === addr) {
    try { localStorage.setItem('nextep_auth_method', 'magic') } catch {}
    logEvent('guest_upgrade', { result: 'attached' })
    return { status: 'attached' }
  }
  logEvent('guest_upgrade', { result: 'code_sent' })
  return { status: 'code_sent' }
}

// → { ok: true } | { ok: false, message }
export async function verifyUpgradeCode(supabase, emailRaw, codeRaw) {
  const addr = String(emailRaw || '').trim().toLowerCase()
  const token = String(codeRaw || '').replace(/\D/g, '')
  if (token.length < 6) return { ok: false, message: 'Enter the 6-digit code from the email.' }
  const { data, error } = await supabase.auth.verifyOtp({ email: addr, token, type: 'email_change' })
  if (error || !data?.session) {
    logEvent('guest_upgrade', { result: 'code_fail' })
    return { ok: false, message: /expired|invalid|token/i.test(error?.message || '') ? 'That code is wrong or has expired. Resend and use the newest email.' : 'That code didn’t work. Try again.' }
  }
  try { localStorage.setItem('nextep_auth_method', 'magic') } catch {}
  logEvent('guest_upgrade', { result: 'verified' })
  return { ok: true }
}

// Days left on the guest lease (purge-guest-sessions keeps anonymous users 30 days).
export const GUEST_LEASE_DAYS = 30
export function guestDaysLeft(user) {
  const t = user?.created_at ? new Date(user.created_at).getTime() : Date.now()
  return Math.max(0, GUEST_LEASE_DAYS - Math.floor((Date.now() - t) / 86400000))
}

// When the keep-this-list ask is EARNED (Phase 1, 2026-10-18): not on the first mark, but
// once there is something an email would protect — day 5 of the lease, or three marks.
// Pure; the mark count is the caller's (a per-guest local counter).
export const SAVE_ASK_MIN_DAYS = 5
export const SAVE_ASK_MIN_MARKS = 3
export function saveAskEarned(user, markCount) {
  const age = GUEST_LEASE_DAYS - guestDaysLeft(user)
  return age >= SAVE_ASK_MIN_DAYS || (markCount | 0) >= SAVE_ASK_MIN_MARKS
}

// Return visit (17.150): a guest who comes back on a later day with a library is the one
// worth asking. 60-day read 2026-10-30: 14 guests marked episodes, 5 saw the mark-triggered
// ask; the two heaviest (193 marks over 5 days, 376 shows over 4 days) never did. Due when
// the account is at least a day old, there is something to keep, and nobody has asked yet.
export const RETURN_ASK_MIN_AGE_MS = 20 * 3600 * 1000
export function returnAskDue(user, shows, now = Date.now()) {
  if (!user?.is_anonymous || !(shows > 0)) return false
  const t = user?.created_at ? new Date(user.created_at).getTime() : now
  return now - t >= RETURN_ASK_MIN_AGE_MS
}

// The inventory a guest would lose: what an email keeps. Pure; lines only, no ask.
export function savingKeeps({ shows = 0, streak = null, following = 0, pushOn = false, cards = 0 } = {}) {
  const out = []
  if (shows > 0) out.push(`${shows} show${shows === 1 ? '' : 's'} and where you are in each`)
  if (streak?.tier && streak?.game) out.push(`Your ${streak.tier} ${streak.game} streak`)
  if (following > 0) out.push(`${following} ${following === 1 ? 'person' : 'people'} you follow, and who’s ahead of you`)
  if (pushOn) out.push('Reminders on this phone')
  if (cards > 0) out.push(`${cards} Final Cut card${cards === 1 ? '' : 's'}`)
  return out
}
